import { uploadTemporaryFile } from '@tyro/api';
import { useTranslation } from '@tyro/i18n';
import { nanoid } from 'nanoid';
import { toast } from 'sonner';
import { extractDocumentCSS } from '../utils/extract-document-css';
import { getReactAsHtml } from '../utils/get-react-as-html';

export async function exportReactToPdf(
  componentGetter: () => React.ReactElement | Promise<React.ReactElement>,
  additionalCss = '',
) {
  const component = await componentGetter();
  const html = getReactAsHtml(component);

  const extractedCSS = extractDocumentCSS();

  const fullHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${document.title}</title>
          <style>
            ${extractedCSS}
            ${additionalCss}
          </style>
        </head>
        <body>
          ${html}
        </body>
        <script>
          window.print();
        </script>
        </html>
      `;

  const file = new File([fullHTML], `${nanoid(8)}.html`, {
    type: 'text/html',
  });

  const fileResponse = await uploadTemporaryFile(file);

  if (fileResponse?.fileUrl) {
    window.open(fileResponse?.fileUrl, '_blank', 'noreferrer');
  } else {
    throw new Error('No file');
  }
}

export function useExportReactToPdf() {
  const { t } = useTranslation(['common']);

  return {
    exportReactToPdf: (
      componentGetter: () => React.ReactElement | Promise<React.ReactElement>,
      additionalCss?: string,
    ) =>
      toast.promise(exportReactToPdf(componentGetter, additionalCss), {
        loading: t('common:exporting'),
        error: t('common:exportFailed'),
      }),
  };
}
